import { useState, useRef, useCallback, useEffect } from 'react'

/**
 * Accumulate speech-recognition segments and send them as ONE message after a
 * configurable quiet period — decoupling "send" from the browser endpointer.
 *
 * The Web Speech API finalizes an utterance after ~1–2s of silence and that
 * is not tunable, so sending on recognition end fired mid-thought whenever
 * the speaker paused to compose the next clause. Instead every finalized
 * segment is appended to a pending buffer and (re)arms a send timer; any new
 * speech — a fresh segment, or interim speech in progress via hold() — pushes
 * the deadline back. The message goes out only after `delayMs` of true quiet.
 */
export function useVoiceSendBuffer({
  delayMs,
  onSend,
}: {
  delayMs: number
  onSend: (text: string) => void
}) {
  const [pending, setPending] = useState('')
  const pendingRef = useRef('')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Callback/delay in refs so changing them never drops an armed timer.
  const onSendRef = useRef(onSend)
  onSendRef.current = onSend
  const delayRef = useRef(delayMs)
  delayRef.current = delayMs

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  const flush = useCallback(() => {
    clearTimer()
    const text = pendingRef.current.trim()
    pendingRef.current = ''
    setPending('')
    if (text) onSendRef.current(text)
  }, [])

  const arm = useCallback(() => {
    clearTimer()
    timerRef.current = setTimeout(flush, delayRef.current)
  }, [flush])

  /** A finalized utterance segment — append and (re)arm the quiet timer. */
  const addSegment = useCallback((text: string) => {
    const t = text.trim()
    if (!t) return
    pendingRef.current = pendingRef.current ? `${pendingRef.current} ${t}` : t
    setPending(pendingRef.current)
    arm()
  }, [arm])

  /** Speech is in progress right now — postpone the send deadline. */
  const hold = useCallback(() => {
    if (pendingRef.current) arm()
  }, [arm])

  /** Send whatever is pending immediately (tap-to-send). */
  const sendNow = useCallback(() => flush(), [flush])

  /** Drop the pending buffer without sending. */
  const cancel = useCallback(() => {
    clearTimer()
    pendingRef.current = ''
    setPending('')
  }, [])

  useEffect(() => () => clearTimer(), [])

  return { pending, addSegment, hold, sendNow, cancel }
}
